import { AUDIO_CONFIG } from "../config/audio.ts";
import type {
  AudioSurfaceKind,
  InteriorAcousticProfile,
  RiverIdentity,
} from "./types.ts";
import { surfaceVoiceProfile } from "./surfaces.ts";

export interface ProceduralVoice {
  stop(): void;
}

export interface ProceduralAmbience {
  setLevel(level: number, now: number): void;
  dispose(): void;
}

export interface ProceduralVehicleAudio {
  update(
    levels: Readonly<{ engine: number; tyres: number; horn: number; siren: number }>,
    enginePlaybackRate: number,
    now: number,
  ): void;
  dispose(): void;
}

interface ProceduralControllableLoop extends ProceduralAmbience {
  setPlaybackRate(playbackRate: number, now: number): void;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function hashText(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function nextNoise(state: { value: number }): number {
  state.value = (Math.imul(state.value, 1_664_525) + 1_013_904_223) >>> 0;
  return state.value / 0xffff_ffff * 2 - 1;
}

function createNoiseBuffer(
  context: AudioContext,
  durationSeconds: number,
  seed: number,
  envelope: (progress: number) => number,
  colour: (raw: number, previous: number) => number,
): AudioBuffer {
  const sampleCount = Math.max(
    1,
    Math.ceil(durationSeconds * AUDIO_CONFIG.noiseSampleRate),
  );
  const buffer = context.createBuffer(
    1,
    sampleCount,
    AUDIO_CONFIG.noiseSampleRate,
  );
  const samples = buffer.getChannelData(0);
  const random = { value: seed || 1 };
  let previous = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const progress = index / samples.length;
    const raw = nextNoise(random);
    previous = colour(raw, previous);
    samples[index] = previous * envelope(progress);
  }
  return buffer;
}

function createFootstepBuffer(
  context: AudioContext,
  surface: AudioSurfaceKind,
): AudioBuffer {
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.footstepDurationSeconds,
    hashText(`vilnius-footstep:${surface}`),
    (progress) =>
      (1 - Math.exp(-progress * AUDIO_CONFIG.footstepVoice.attackRate)) *
      Math.exp(
        -progress * AUDIO_CONFIG.footstepVoice.envelopeDecay,
      ),
    (raw, previous) => {
      const memory = surfaceVoiceProfile(surface).noiseMemory;
      return previous * memory + raw * (1 - memory);
    },
  );
}

function createLandingBuffer(context: AudioContext): AudioBuffer {
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.landingDurationSeconds,
    hashText("vilnius-landing"),
    (progress) =>
      Math.exp(-progress * AUDIO_CONFIG.landingVoice.envelopeDecay),
    (raw, previous) =>
      previous * AUDIO_CONFIG.landingVoice.noiseMemory +
      raw * (1 - AUDIO_CONFIG.landingVoice.noiseMemory),
  );
}

function createBellBuffer(context: AudioContext): AudioBuffer {
  const duration = AUDIO_CONFIG.bellDurationSeconds;
  const sampleCount = Math.max(
    1,
    Math.ceil(duration * AUDIO_CONFIG.noiseSampleRate),
  );
  const buffer = context.createBuffer(
    1,
    sampleCount,
    AUDIO_CONFIG.noiseSampleRate,
  );
  const samples = buffer.getChannelData(0);
  for (let index = 0; index < samples.length; index += 1) {
    const time = index / AUDIO_CONFIG.noiseSampleRate;
    let sample = 0;
    for (const partial of AUDIO_CONFIG.bellVoice.partials) {
      sample += Math.sin(time * partial.hertz * Math.PI * 2) *
        partial.amplitude *
        Math.exp(-time * partial.decay);
    }
    const attack = Math.min(1, time * AUDIO_CONFIG.bellVoice.attackRate);
    samples[index] =
      sample * attack * AUDIO_CONFIG.bellVoice.outputScale;
  }
  return buffer;
}

function createToneBuffer(
  context: AudioContext,
  durationSeconds: number,
  sampleAt: (timeSeconds: number) => number,
): AudioBuffer {
  const sampleCount = Math.max(
    1,
    Math.ceil(durationSeconds * AUDIO_CONFIG.noiseSampleRate),
  );
  const buffer = context.createBuffer(1, sampleCount, AUDIO_CONFIG.noiseSampleRate);
  const samples = buffer.getChannelData(0);
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = sampleAt(index / AUDIO_CONFIG.noiseSampleRate);
  }
  return buffer;
}

function createVehicleEngineBuffer(context: AudioContext): AudioBuffer {
  return createToneBuffer(
    context,
    AUDIO_CONFIG.vehicleEngineLoopSeconds,
    (time) =>
      Math.sin(time * 48 * Math.PI * 2) * 0.48 +
      Math.sin(time * 96 * Math.PI * 2) * 0.25 +
      Math.sin(time * 144 * Math.PI * 2) * 0.12,
  );
}

function createVehicleTyreBuffer(context: AudioContext): AudioBuffer {
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.vehicleTyreLoopSeconds,
    hashText("vilnius-vehicle-tyres"),
    () => 1,
    (raw, previous) => previous * 0.58 + raw * 0.42,
  );
}

function createVehicleSirenBuffer(context: AudioContext): AudioBuffer {
  return createToneBuffer(
    context,
    AUDIO_CONFIG.vehicleSirenLoopSeconds,
    (time) => {
      const halfCycle = AUDIO_CONFIG.vehicleSirenLoopSeconds / 4;
      const high = Math.floor(time / halfCycle) % 2 === 1;
      const frequency = high ? 620 : 470;
      return Math.sin(time * frequency * Math.PI * 2) * 0.52;
    },
  );
}

function createVehicleHornBuffer(context: AudioContext): AudioBuffer {
  return createToneBuffer(
    context,
    AUDIO_CONFIG.vehicleHornLoopSeconds,
    (time) =>
      Math.sin(time * 390 * Math.PI * 2) * 0.38 +
      Math.sin(time * 520 * Math.PI * 2) * 0.25,
  );
}

function createVehicleImpactBuffer(context: AudioContext): AudioBuffer {
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.vehicleImpactDurationSeconds,
    hashText("vilnius-vehicle-impact"),
    (progress) => Math.exp(-progress * 9),
    (raw, previous) => previous * 0.44 + raw * 0.56,
  );
}

function createWindBuffer(context: AudioContext): AudioBuffer {
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.windLoopSeconds,
    hashText("vilnius-summer-wind"),
    () => 1,
    (raw, previous) =>
      previous * AUDIO_CONFIG.ambienceNoiseMemory.wind +
      raw * (1 - AUDIO_CONFIG.ambienceNoiseMemory.wind),
  );
}

function createRiverBuffer(
  context: AudioContext,
  river: RiverIdentity,
): AudioBuffer {
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.riverLoopSeconds,
    hashText(`vilnius-river:${river}`),
    () => 1,
    river === "vilnia"
      ? (raw, previous) =>
          previous * AUDIO_CONFIG.ambienceNoiseMemory.vilnia +
          raw * (1 - AUDIO_CONFIG.ambienceNoiseMemory.vilnia)
      : (raw, previous) =>
          previous * AUDIO_CONFIG.ambienceNoiseMemory.neris +
          raw * (1 - AUDIO_CONFIG.ambienceNoiseMemory.neris),
  );
}

export function foodHallInteriorSample(
  timeSeconds: number,
  rawNoise: number,
  smoothedNoise: number,
): number {
  const voice = AUDIO_CONFIG.foodHallVoice;
  const sizzle = rawNoise * voice.sizzleGain *
    (0.7 + Math.sin(timeSeconds * Math.PI * 2 * voice.sizzlePulseHertz) * 0.3);
  const espressoProgress =
    (timeSeconds - voice.espressoStartSeconds) /
    voice.espressoDurationSeconds;
  const espresso = espressoProgress > 0 && espressoProgress < 1
    ? rawNoise * voice.espressoGain * Math.sin(espressoProgress * Math.PI)
    : 0;
  let clinks = 0;
  for (const clink of voice.clinks) {
    const elapsed = timeSeconds - clink.atSeconds;
    if (elapsed >= 0 && elapsed <= 0.22) {
      clinks += Math.sin(elapsed * clink.hertz * Math.PI * 2) *
        Math.exp(-elapsed * voice.clinkDecay) * voice.clinkGain;
    }
  }
  return Math.max(
    -1,
    Math.min(1, smoothedNoise * voice.murmurGain + sizzle + espresso + clinks),
  );
}

function createFoodHallBuffer(context: AudioContext): AudioBuffer {
  const sampleCount = Math.max(
    1,
    Math.ceil(AUDIO_CONFIG.interiorLoopSeconds * AUDIO_CONFIG.noiseSampleRate),
  );
  const buffer = context.createBuffer(
    1,
    sampleCount,
    AUDIO_CONFIG.noiseSampleRate,
  );
  const samples = buffer.getChannelData(0);
  const random = { value: hashText("vilnius-interior:food-hall") || 1 };
  let smoothed = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const raw = nextNoise(random);
    smoothed = smoothed * AUDIO_CONFIG.ambienceNoiseMemory.foodHall +
      raw * (1 - AUDIO_CONFIG.ambienceNoiseMemory.foodHall);
    samples[index] = foodHallInteriorSample(
      index / AUDIO_CONFIG.noiseSampleRate,
      raw,
      smoothed,
    );
  }
  return buffer;
}

function createInteriorBuffer(
  context: AudioContext,
  profile: InteriorAcousticProfile,
): AudioBuffer {
  if (profile === "food-hall") {
    return createFoodHallBuffer(context);
  }
  const memory = profile === "cathedral-nave"
    ? AUDIO_CONFIG.ambienceNoiseMemory.cathedralNave
    : profile === "airport-terminal"
      ? AUDIO_CONFIG.ambienceNoiseMemory.airportTerminal
      : profile === "music-venue"
        ? AUDIO_CONFIG.ambienceNoiseMemory.musicVenue
        : profile === "railway-hall"
          ? AUDIO_CONFIG.ambienceNoiseMemory.railwayHall
          : profile === "bus-terminal"
            ? AUDIO_CONFIG.ambienceNoiseMemory.busTerminal
            : profile === "historic-bank"
              ? AUDIO_CONFIG.ambienceNoiseMemory.historicBank
              : profile === "prison-cellblock"
                ? AUDIO_CONFIG.ambienceNoiseMemory.prisonCellblock
                : profile === "tower-lobby"
                  ? AUDIO_CONFIG.ambienceNoiseMemory.towerLobby
                  : profile === "tower-observation"
                    ? AUDIO_CONFIG.ambienceNoiseMemory.towerObservation
                    : AUDIO_CONFIG.ambienceNoiseMemory.civicLobby;
  return createNoiseBuffer(
    context,
    AUDIO_CONFIG.interiorLoopSeconds,
    hashText(`vilnius-interior:${profile}`),
    profile === "bus-terminal"
      ? (progress) =>
          0.78 +
          Math.sin(progress * Math.PI * 8) * 0.08 +
          Math.max(0, Math.sin(progress * Math.PI * 2 - 0.9)) * 0.12
      : () => 1,
    (raw, previous) => previous * memory + raw * (1 - memory),
  );
}

function scheduleBufferVoice(
  context: AudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  options: Readonly<{
    gain: number;
    playbackRate: number;
    highpassHertz: number;
    lowpassHertz: number;
    durationSeconds: number;
  }>,
  onEnded: () => void,
): ProceduralVoice {
  const source = context.createBufferSource();
  const highpass = context.createBiquadFilter();
  const lowpass = context.createBiquadFilter();
  const gain = context.createGain();
  const now = context.currentTime;
  let stopped = false;

  source.buffer = buffer;
  source.playbackRate.value = options.playbackRate;
  highpass.type = "highpass";
  highpass.frequency.value = options.highpassHertz;
  lowpass.type = "lowpass";
  lowpass.frequency.value = options.lowpassHertz;
  gain.gain.setValueAtTime(Math.max(0.0001, options.gain), now);
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    now + options.durationSeconds,
  );

  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(gain);
  gain.connect(destination);

  const cleanup = (): void => {
    if (stopped) {
      return;
    }
    stopped = true;
    source.disconnect();
    highpass.disconnect();
    lowpass.disconnect();
    gain.disconnect();
    onEnded();
  };
  source.onended = cleanup;
  source.start(now);
  source.stop(now + options.durationSeconds);

  return {
    stop(): void {
      if (stopped) {
        return;
      }
      try {
        source.stop();
      } catch {
        // A naturally ended source is already disconnected by `cleanup`.
      }
      cleanup();
    },
  };
}

export class ProceduralAudioBank {
  readonly #context: AudioContext;
  readonly #destination: AudioNode;
  readonly #footsteps = new Map<AudioSurfaceKind, AudioBuffer>();
  readonly #interiors = new Map<InteriorAcousticProfile, AudioBuffer>();
  #landing: AudioBuffer | null = null;
  #bell: AudioBuffer | null = null;
  #vehicleImpact: AudioBuffer | null = null;

  constructor(context: AudioContext, destination: AudioNode) {
    this.#context = context;
    this.#destination = destination;
  }

  startFootstep(
    eventId: string,
    surface: AudioSurfaceKind,
    onEnded: () => void,
  ): ProceduralVoice {
    let buffer = this.#footsteps.get(surface);
    if (buffer === undefined) {
      buffer = createFootstepBuffer(this.#context, surface);
      this.#footsteps.set(surface, buffer);
    }
    const profile = surfaceVoiceProfile(surface);
    const variation = hashText(eventId) % 13 - 6;
    return scheduleBufferVoice(
      this.#context,
      this.#destination,
      buffer,
      {
        gain: AUDIO_CONFIG.footstepGain * profile.gainScale,
        playbackRate:
          profile.playbackRate *
          (
            1 +
            variation * AUDIO_CONFIG.footstepVoice.pitchVariationStep
          ),
        highpassHertz: profile.highpassHertz,
        lowpassHertz: profile.lowpassHertz,
        durationSeconds: AUDIO_CONFIG.footstepDurationSeconds,
      },
      onEnded,
    );
  }

  startLanding(eventId: string, onEnded: () => void): ProceduralVoice {
    this.#landing ??= createLandingBuffer(this.#context);
    const variation = hashText(eventId) % 9 - 4;
    return scheduleBufferVoice(
      this.#context,
      this.#destination,
      this.#landing,
      {
        gain: AUDIO_CONFIG.landingGain,
        playbackRate:
          AUDIO_CONFIG.landingVoice.playbackRate +
          variation * AUDIO_CONFIG.landingVoice.pitchVariationStep,
        highpassHertz: AUDIO_CONFIG.landingVoice.highpassHertz,
        lowpassHertz: AUDIO_CONFIG.landingVoice.lowpassHertz,
        durationSeconds: AUDIO_CONFIG.landingDurationSeconds,
      },
      onEnded,
    );
  }

  startBell(
    slotId: string,
    gainScale: number,
    onEnded: () => void,
  ): ProceduralVoice {
    this.#bell ??= createBellBuffer(this.#context);
    const variation = hashText(slotId) % 5 - 2;
    return scheduleBufferVoice(
      this.#context,
      this.#destination,
      this.#bell,
      {
        gain: AUDIO_CONFIG.bellGain * clamp01(gainScale),
        playbackRate:
          AUDIO_CONFIG.bellVoice.playbackRate +
          variation * AUDIO_CONFIG.bellVoice.pitchVariationStep,
        highpassHertz: AUDIO_CONFIG.bellVoice.highpassHertz,
        lowpassHertz: AUDIO_CONFIG.bellVoice.lowpassHertz,
        durationSeconds: AUDIO_CONFIG.bellDurationSeconds,
      },
      onEnded,
    );
  }

  startWind(): ProceduralAmbience {
    return createLoop(
      this.#context,
      this.#destination,
      createWindBuffer(this.#context),
      "lowpass",
      AUDIO_CONFIG.ambienceFilters.windHertz,
    );
  }

  startRiver(river: RiverIdentity): ProceduralAmbience {
    return createLoop(
      this.#context,
      this.#destination,
      createRiverBuffer(this.#context, river),
      "bandpass",
      river === "vilnia"
        ? AUDIO_CONFIG.ambienceFilters.vilniaHertz
        : AUDIO_CONFIG.ambienceFilters.nerisHertz,
    );
  }

  startInterior(profile: InteriorAcousticProfile): ProceduralAmbience {
    let buffer = this.#interiors.get(profile);
    if (buffer === undefined) {
      buffer = createInteriorBuffer(this.#context, profile);
      this.#interiors.set(profile, buffer);
    }
    const filterType: BiquadFilterType =
      profile === "cathedral-nave" || profile === "tower-observation"
        ? "bandpass"
        : "lowpass";
    const filterHertz = profile === "cathedral-nave"
      ? AUDIO_CONFIG.ambienceFilters.cathedralNaveHertz
      : profile === "airport-terminal"
        ? AUDIO_CONFIG.ambienceFilters.airportTerminalHertz
      : profile === "food-hall"
        ? AUDIO_CONFIG.ambienceFilters.foodHallHertz
      : profile === "music-venue"
        ? AUDIO_CONFIG.ambienceFilters.musicVenueHertz
      : profile === "railway-hall"
        ? AUDIO_CONFIG.ambienceFilters.railwayHallHertz
      : profile === "bus-terminal"
        ? AUDIO_CONFIG.ambienceFilters.busTerminalHertz
      : profile === "historic-bank"
        ? AUDIO_CONFIG.ambienceFilters.historicBankHertz
      : profile === "prison-cellblock"
        ? AUDIO_CONFIG.ambienceFilters.prisonCellblockHertz
        : profile === "tower-lobby"
          ? AUDIO_CONFIG.ambienceFilters.towerLobbyHertz
          : profile === "tower-observation"
            ? AUDIO_CONFIG.ambienceFilters.towerObservationHertz
            : AUDIO_CONFIG.ambienceFilters.civicLobbyHertz;
    return createLoop(
      this.#context,
      this.#destination,
      buffer,
      filterType,
      filterHertz,
    );
  }

  startVehicle(): ProceduralVehicleAudio {
    const engine = createControllableLoop(
      this.#context,
      this.#destination,
      createVehicleEngineBuffer(this.#context),
      "lowpass",
      1_250,
    );
    const tyres = createControllableLoop(
      this.#context,
      this.#destination,
      createVehicleTyreBuffer(this.#context),
      "bandpass",
      1_450,
    );
    const horn = createControllableLoop(
      this.#context,
      this.#destination,
      createVehicleHornBuffer(this.#context),
      "bandpass",
      780,
    );
    const siren = createControllableLoop(
      this.#context,
      this.#destination,
      createVehicleSirenBuffer(this.#context),
      "bandpass",
      920,
    );
    return {
      update(levels, enginePlaybackRate, now): void {
        engine.setLevel(levels.engine, now);
        engine.setPlaybackRate(enginePlaybackRate, now);
        tyres.setLevel(levels.tyres, now);
        horn.setLevel(levels.horn, now);
        siren.setLevel(levels.siren, now);
      },
      dispose(): void {
        engine.dispose();
        tyres.dispose();
        horn.dispose();
        siren.dispose();
      },
    };
  }

  startVehicleImpact(
    intensity01: number,
    onEnded: () => void,
  ): ProceduralVoice {
    this.#vehicleImpact ??= createVehicleImpactBuffer(this.#context);
    return scheduleBufferVoice(
      this.#context,
      this.#destination,
      this.#vehicleImpact,
      {
        gain: AUDIO_CONFIG.vehicleImpactGain * clamp01(intensity01),
        playbackRate: 0.82 + clamp01(intensity01) * 0.35,
        highpassHertz: 65,
        lowpassHertz: 2_600,
        durationSeconds: AUDIO_CONFIG.vehicleImpactDurationSeconds,
      },
      onEnded,
    );
  }
}

function createLoop(
  context: AudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  filterType: BiquadFilterType,
  filterHertz: number,
): ProceduralAmbience {
  return createControllableLoop(
    context,
    destination,
    buffer,
    filterType,
    filterHertz,
  );
}

function createControllableLoop(
  context: AudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  filterType: BiquadFilterType,
  filterHertz: number,
): ProceduralControllableLoop {
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  let disposed = false;

  source.buffer = buffer;
  source.loop = true;
  filter.type = filterType;
  filter.frequency.value = filterHertz;
  gain.gain.value = 0;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(destination);
  source.start();

  return {
    setLevel(level: number, now: number): void {
      if (disposed) {
        return;
      }
      gain.gain.setTargetAtTime(
        clamp01(level),
        now,
        AUDIO_CONFIG.ambienceSmoothingSeconds,
      );
    },
    setPlaybackRate(playbackRate: number, now: number): void {
      if (disposed) return;
      source.playbackRate.setTargetAtTime(
        Math.max(0.05, playbackRate),
        now,
        AUDIO_CONFIG.vehicleLoopSmoothingSeconds,
      );
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      try {
        source.stop();
      } catch {
        // An already-stopped loop only needs its graph disconnected.
      }
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    },
  };
}
